import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle2, CalendarClock, XCircle, PackageCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$ ${Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS = {
  sent: { label: "Pendiente de confirmación", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  confirmed: { label: "Confirmada", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  proposed: { label: "Nueva fecha propuesta", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  rejected: { label: "Rechazada", cls: "bg-red-100 text-red-800 border-red-300" },
};

export default function PoConfirm() {
  const { token } = useParams();
  const [po, setPo] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("confirm");
  const [date, setDate] = useState("");
  const [comments, setComments] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  const load = () => axios.get(`${API}/public/po-confirm/${token}`).then((r) => { setPo(r.data); setDate(r.data.delivery_date || ""); })
    .catch((e) => setError(e.response?.data?.detail || "Enlace inválido"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/public/po-confirm/${token}`, { action: mode, delivery_date: mode === "propose" ? date : "", comments, contact_name: name });
      setDone(data.status); load();
    } catch (e) { setError(e.response?.data?.detail || "No se pudo enviar la respuesta"); }
    finally { setBusy(false); }
  };

  if (error && !po) return <div className="min-h-screen flex items-center justify-center text-sm text-red-700" data-testid="po-confirm-error">{error}</div>;
  if (!po) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Cargando…</div>;
  const conf = po.confirmation || {};
  const st = STATUS[conf.status] || STATUS.sent;
  const answered = ["confirmed", "proposed", "rejected"].includes(conf.status);

  return (
    <div className="min-h-screen bg-[#f6f6f4] text-[#111827] py-8 px-4" data-testid="po-confirm-page">
      <div className="max-w-3xl mx-auto bg-white border shadow-sm">
        <div className="border-b-4 border-[#B91C1C] p-6 flex justify-between items-start gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#B91C1C]">{po.company_name || "Orden de compra"}</div>
            <h1 className="text-2xl font-black uppercase mt-1">Orden de Compra {po.number}</h1>
            <div className="text-xs text-slate-500 mt-1">Proveedor: <b>{po.supplier_name}</b> · Moneda {po.currency}</div>
          </div>
          <span className={`text-[10px] font-bold uppercase border px-2 py-1 ${st.cls}`} data-testid="po-confirm-status">{st.label}</span>
        </div>
        <div className="p-6 grid gap-6 md:grid-cols-[1fr_260px]">
          <div>
            <table className="w-full text-xs">
              <thead><tr className="bg-[#B91C1C] text-white text-left">
                <th className="p-2">No. Parte</th><th className="p-2">Producto</th><th className="p-2 text-right">Cant.</th><th className="p-2 text-right">P. Unit</th><th className="p-2 text-right">Total</th>
              </tr></thead>
              <tbody>
                {po.lines.map((l, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-2 font-bold">{l.item_number}</td><td className="p-2">{l.description}</td>
                    <td className="p-2 text-right">{l.qty} {l.uom}</td><td className="p-2 text-right">{money(l.unit_cost)}</td>
                    <td className="p-2 text-right">{money(l.qty * l.unit_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end mt-3">
              <div className="text-xs space-y-0.5 text-right">
                <div>Subtotal: <b>{money(po.subtotal)}</b></div>
                <div>IVA {po.tax_rate ?? 0}%: <b>{money(po.tax_amount)}</b></div>
                <div className="text-base">Total {po.currency}: <b>{money(po.total)}</b></div>
              </div>
            </div>
            {po.notes && <div className="text-xs mt-4 border p-3 bg-slate-50"><b>Instrucciones:</b> {po.notes}</div>}
          </div>
          <div className="text-xs space-y-3">
            <div className="border p-3"><div className="text-[10px] font-bold uppercase text-slate-500">Fecha de entrega solicitada</div><div className="text-lg font-black" data-testid="po-confirm-delivery">{po.delivery_date || "—"}</div></div>
            {po.payment_terms && <div className="border p-3"><div className="text-[10px] font-bold uppercase text-slate-500">Condiciones de pago</div><div className="font-bold">{po.payment_terms}</div></div>}
            {po.quotation_number && <div className="border p-3"><div className="text-[10px] font-bold uppercase text-slate-500">Su cotización</div><div className="font-bold">{po.quotation_number}</div></div>}
            {po.ship_to_warehouse && <div className="border p-3"><div className="text-[10px] font-bold uppercase text-slate-500">Entregar en</div><div className="font-bold">{po.ship_to_warehouse}</div></div>}
          </div>
        </div>

        <div className="border-t p-6 bg-slate-50">
          {answered ? (
            <div className="flex items-start gap-3" data-testid="po-confirm-done">
              <PackageCheck className="text-emerald-600 shrink-0" />
              <div className="text-sm">
                <div className="font-black uppercase">{done ? "¡Gracias! Su respuesta fue enviada." : "Esta orden ya fue respondida."}</div>
                <div className="text-xs text-slate-600 mt-1">
                  Estado: <b>{st.label}</b>{conf.proposed_delivery_date && <> · Fecha propuesta: <b>{conf.proposed_delivery_date}</b></>}
                  {conf.contact_name && <> · Por: {conf.contact_name}</>}{conf.comments && <> · "{conf.comments}"</>}
                </div>
                {po.company_email && <div className="text-xs text-slate-500 mt-1">Dudas: {po.company_email}</div>}
              </div>
            </div>
          ) : (
            <>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Confirme la orden y la fecha de entrega</div>
              <div className="flex gap-2 flex-wrap mb-4">
                {[["confirm", "Confirmo la orden y la fecha", CheckCircle2], ["propose", "Propongo otra fecha", CalendarClock], ["reject", "No puedo surtir", XCircle]].map(([k, l, Icon]) => (
                  <button key={k} onClick={() => setMode(k)} data-testid={`po-confirm-mode-${k}`}
                    className={`flex items-center gap-2 text-xs font-bold px-3 py-2 border transition-colors ${mode === k ? "bg-[#B91C1C] text-white border-[#B91C1C]" : "bg-white hover:bg-slate-100"}`}>
                    <Icon size={14} /> {l}
                  </button>
                ))}
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                {mode === "propose" && (
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Nueva fecha de entrega</Label>
                    <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none h-9 text-xs" data-testid="po-confirm-date" />
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase">Su nombre</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none h-9 text-xs" data-testid="po-confirm-name" />
                </div>
                <div className={`space-y-1 ${mode === "propose" ? "" : "md:col-span-2"}`}>
                  <Label className="text-[10px] font-bold uppercase">Comentarios{mode === "reject" ? " (motivo, obligatorio)" : ""}</Label>
                  <Textarea value={comments} rows={2} onChange={(e) => setComments(e.target.value)} className="rounded-none text-xs" data-testid="po-confirm-comments" />
                </div>
              </div>
              {error && <div className="text-xs text-red-700 mt-2" data-testid="po-confirm-error">{error}</div>}
              <Button onClick={submit} disabled={busy || (mode === "propose" && !date) || (mode === "reject" && !comments.trim())}
                className="rounded-none font-bold mt-4 bg-[#B91C1C] hover:bg-[#991B1B]" data-testid="po-confirm-submit">
                {busy ? "Enviando…" : "Enviar respuesta"}
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="text-center text-[10px] text-slate-400 mt-4">Este enlace es personal para esta orden de compra. No requiere usuario ni contraseña.</div>
    </div>
  );
}
